/* TheYayaCafe — small, dependency-free behaviours (countdown, gift finder,
   personalise preview, sticky CTA, newsletter). All progressive-enhancement:
   markup works without JS; these add interactivity. */
(function () {
  'use strict';

  /* ---------- Countdown ----------------------------------------------------
     <tyyc-countdown deadline="2026-06-21T00:00:00+05:30" variant="light"></tyyc-countdown>
     Renders Days / Hrs / Min cells, updates each second, hides itself once past. */
  class TyycCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = new Date(this.getAttribute('deadline')).getTime();
      if (isNaN(this.deadline)) { this.hidden = true; return; }
      this.variant = this.getAttribute('variant') === 'dark' ? 'dark' : 'light';
      this.render();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    render() {
      const cell = (id, label) =>
        `<div class="tyyc-cd__cell tyyc-cd__cell--${this.variant}">` +
        `<span class="tyyc-cd__num tabular" data-${id}>—</span>` +
        `<span class="tyyc-cd__lbl">${label}</span></div>`;
      this.innerHTML = `<div class="tyyc-cd">${cell('d', 'Days')}${cell('h', 'Hrs')}${cell('m', 'Min')}</div>`;
      this.$d = this.querySelector('[data-d]');
      this.$h = this.querySelector('[data-h]');
      this.$m = this.querySelector('[data-m]');
    }
    tick() {
      const diff = this.deadline - Date.now();
      if (diff <= 0) { this.hidden = true; clearInterval(this.timer); return; }
      const pad = (n) => String(n).padStart(2, '0');
      this.$d.textContent = pad(Math.floor(diff / 86400000));
      this.$h.textContent = pad(Math.floor((diff / 3600000) % 24));
      this.$m.textContent = pad(Math.floor((diff / 60000) % 60));
    }
  }
  if (!customElements.get('tyyc-countdown')) customElements.define('tyyc-countdown', TyycCountdown);

  /* ---------- Personalise live preview -------------------------------------
     Mirrors a text input into a preview node within the same [data-personalise]. */
  document.querySelectorAll('[data-personalise]').forEach((root) => {
    const input = root.querySelector('[data-personalise-input]');
    const out = root.querySelector('[data-personalise-out]');
    if (!input || !out) return;
    const ph = out.textContent;
    input.addEventListener('input', () => {
      out.textContent = (input.value || '').toUpperCase() || ph;
    });
  });

  /* ---------- Dynamic gift finder (single screen) --------------------------
     Three [data-answer] selects (recipient/occasion/interest); each defaults to
     an "Any" option (value "*") = no filter for that facet.

     Shopify hides storefront filters on collections >5,000 products (and never
     builds them for the virtual /collections/all), so we can't filter one big
     collection. Instead there is a real collection per facet value — handle
     `gf-<facet>-<value>` (e.g. gf-recipient-dad, gf-occasion-fathers-day) — each
     under the cap, so its filters work. The button LANDS on the collection of the
     first chosen facet (priority recipient > occasion > interest) and applies the
     remaining chosen facets as filter.p.m.custom.<facet>=<value> params, which the
     landing collection honours because it is under 5,000. Nothing chosen → base.
     Picking a recipient also gates the occasion + interest options. */
  document.querySelectorAll('[data-gift-finder]').forEach((root) => {
    const selects = [...root.querySelectorAll('[data-answer]')];
    if (!selects.length) return;
    const submit = root.querySelector('[data-submit]');
    const base = (root.getAttribute('data-base') || root.getAttribute('data-all-products') || '/collections/all').split('?')[0];
    const ns = root.getAttribute('data-ns') || 'custom';
    const prefix = root.getAttribute('data-collection-prefix') || 'gf';
    const FACET_ORDER = ['recipient', 'occasion', 'interest'];

    const selOf = (f) => selects.find((s) => s.getAttribute('data-facet') === f);
    const valOf = (f) => { const s = selOf(f); const v = s ? s.value : ''; return v === '*' || v === '' ? '' : v; };

    const buildUrl = () => {
      const chosen = FACET_ORDER.filter((f) => valOf(f));        // non-Any, in priority order
      if (!chosen.length) return base;                           // nothing picked → all products
      const landing = chosen[0];
      const collUrl = `/collections/${prefix}-${landing}-${valOf(landing)}`;
      const params = chosen.slice(1).map((f) => `filter.p.m.${ns}.${f}=${encodeURIComponent(valOf(f))}`);
      return params.length ? `${collUrl}?${params.join('&')}` : collUrl;
    };

    // Relationship-appropriate gating, keyed off the chosen recipient.
    // OCCASION_ONLY_FOR: occasion shown ONLY for these recipients (others hidden).
    // INTEREST_NOT_FOR: interest hidden for these recipients. Values not listed are universal.
    const OCCASION_ONLY_FOR = {
      'fathers-day': ['dad'],
      'mothers-day': ['mom'],
      'raksha-bandhan': ['brother', 'sister', 'bhabhi'],
      'valentines-day': ['husband', 'wife', 'boyfriend', 'girlfriend', 'couple'],
      'karwa-chauth': ['wife'],
    };
    const INTEREST_NOT_FOR = { whiskey: ['kids'], beer: ['kids'], wine: ['kids'] };

    const gate = (sel, rules, mode) => {
      if (!sel) return;
      const r = valOf('recipient');                 // '' when "Any"
      [...sel.options].forEach((o) => {
        if (o.value === '*') { o.hidden = false; o.disabled = false; return; }
        const list = rules[o.value];
        let ok = true;
        if (list && r) ok = mode === 'only' ? list.indexOf(r) >= 0 : list.indexOf(r) < 0;
        o.hidden = !ok; o.disabled = !ok;
      });
      if (sel.selectedOptions[0] && sel.selectedOptions[0].hidden) sel.value = '*';   // reset stale pick
    };
    // The button is a REAL link; we just keep its href in sync with the choices, so
    // clicking always navigates natively (works even if JS later hiccups; with no JS
    // at all it falls back to the base gift collection).
    const updateHref = () => {
      if (!submit) return;
      let dest = buildUrl();
      const pv = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (pv) dest += (dest.indexOf('?') >= 0 ? '&' : '?') + 'preview_theme_id=' + pv;  // stay in preview while testing
      submit.setAttribute('href', dest);
    };
    const sync = () => { gate(selOf('occasion'), OCCASION_ONLY_FOR, 'only'); gate(selOf('interest'), INTEREST_NOT_FOR, 'not'); };
    selects.forEach((s) => s.addEventListener('change', () => {
      if (s.getAttribute('data-facet') === 'recipient') sync();
      updateHref();
    }));
    sync();
    updateHref();
  });

  /* ---------- Sticky CTA bar ------------------------------------------------
     Reveals after scrolling past a threshold. */
  document.querySelectorAll('[data-sticky-cta]').forEach((bar) => {
    const onScroll = () => bar.classList.toggle('is-visible', window.scrollY > 620);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  });

  /* ---------- Newsletter inline success (no backend mock — relies on Shopify
     customer form; this only handles the optional client-side incentive note). */
})();
